import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldStar, Lightning, CheckCircle, X, CaretRight, Spinner } from "@phosphor-icons/react";
import { toast } from "sonner";
import API from "../lib/api";

const STORAGE_KEY = "fixli_dismissed_reassignments";

/**
 * Suggested Reassignments widget — shows jobs assigned to Slow/Fair SLA suppliers
 * where a higher-graded alternative covers the same area + trade.
 *
 * Features:
 *   - Collapsible panel, auto-loads on mount (admin-only).
 *   - "Reassign" button delegates to the correct assignment endpoint per entity type.
 *   - "Dismiss" button stores the entity id in localStorage so it won't nag again.
 *   - Navigate to the source entity via the row link.
 *
 * Place on the Dashboard (admin view) or the Suppliers page.
 */
export default function SuggestedReassignments({ limit = 25, compact = false, className = "" }) {
  const navigate = useNavigate();
  const [items, setItems] = useState(null); // null = initial, [] = none
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]")); }
    catch { return new Set(); }
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/vendor-reassignments/suggestions", { params: { limit } });
      setItems(data?.suggestions || []);
    } catch { setItems([]); }
    finally { setLoading(false); }
  }, [limit]);

  useEffect(() => { load(); }, [load]);

  // Phase 229f — pulse + refresh when relevant notifications fire
  const [pulse, setPulse] = useState(false);
  useEffect(() => {
    const onNotif = (e) => {
      const t = e.detail?.type || "";
      if (t.includes("vendor") || t.includes("reassign") || t.includes("sla") || t.includes("supplier")) {
        setPulse(true);
        load();
        setTimeout(() => setPulse(false), 2400);
      }
    };
    window.addEventListener("fixli:notification", onNotif);
    return () => window.removeEventListener("fixli:notification", onNotif);
  }, [load]);

  const saveDismissed = (next) => {
    setDismissed(next);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify([...next])); } catch { /* quota */ }
  };

  const dismiss = (entity_id) => {
    const next = new Set(dismissed);
    next.add(entity_id);
    saveDismissed(next);
  };

  const reassign = async (item) => {
    if (!window.confirm(
      `Reassign "${item.entity_title}" from ${item.current_supplier_name} (${item.current_grade}) to ${item.suggested_supplier_name} (${item.suggested_grade})?`
    )) return;
    setBusyId(item.entity_id);
    try {
      const newVendorId = item.suggested_supplier_user_id || item.suggested_supplier_id;
      const reason = `Auto-suggested upgrade: ${item.current_grade} → ${item.suggested_grade}`;
      if (item.entity_type === "emergency_job") {
        await API.post(`/emergency-jobs/${item.entity_id}/assign`, { vendor_id: newVendorId, reason });
      } else if (item.entity_type === "job") {
        await API.post(`/jobs/${item.entity_id}/assign-vendor`, { vendor_id: newVendorId, reason });
      } else if (item.entity_type === "work_order") {
        await API.post(`/work-orders/${item.entity_id}/reassign`, { vendor_id: newVendorId, reason });
      } else if (item.entity_type === "task") {
        await API.post(`/tasks/${item.entity_id}/assign`, { user_id: newVendorId, reason });
      }
      toast.success(`Reassigned to ${item.suggested_supplier_name}`);
      dismiss(item.entity_id);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reassignment failed — try the supplier's page manually");
    } finally {
      setBusyId(null);
    }
  };

  const openEntity = (item) => {
    if (item.entity_type === "emergency_job") navigate(`/emergency-jobs/${item.entity_id}`);
    else if (item.entity_type === "job") navigate(`/jobs/${item.entity_id}`);
    else if (item.entity_type === "work_order") navigate(`/work-orders/${item.entity_id}`);
    else if (item.entity_type === "task" && item.project_id) navigate(`/projects/${item.project_id}/tasks/${item.entity_id}`);
  };

  const visible = (items || []).filter(i => !dismissed.has(i.entity_id));

  if (loading && items === null) {
    return (
      <div className={`border border-border rounded-md bg-card p-4 ${className}`} data-testid="suggested-reassignments-loading">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Spinner size={14} className="animate-spin" /> Scanning suppliers…
        </div>
      </div>
    );
  }

  if (!visible.length) return null;

  const gradeClass = (g) => g === "Excellent"
    ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"
    : g === "Good"
    ? "bg-sky-500/15 text-sky-700 dark:text-sky-400"
    : g === "Fair"
    ? "bg-amber-500/15 text-amber-700 dark:text-amber-500"
    : "bg-rose-500/15 text-rose-700 dark:text-rose-400";

  return (
    <div className={`border border-border rounded-md bg-card overflow-hidden transition-all duration-500 ${pulse ? "ring-2 ring-amber-400 shadow-lg" : ""} ${className}`} data-testid="suggested-reassignments">
      <button
        type="button"
        onClick={() => setCollapsed(c => !c)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/30 transition"
      >
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-md bg-amber-500/15 text-amber-600">
            <Lightning size={14} weight="fill" />
          </div>
          <div className="text-left">
            <p className="text-sm font-semibold font-[Chivo]">
              Suggested Reassignments
              <span className="ml-2 text-[11px] font-normal text-muted-foreground">({visible.length})</span>
            </p>
            {!compact && (
              <p className="text-[11px] text-muted-foreground">Jobs assigned to Slow/Fair suppliers with a better alternative in the same area</p>
            )}
          </div>
        </div>
        <CaretRight size={14} className={`text-muted-foreground transition-transform ${collapsed ? "" : "rotate-90"}`} />
      </button>

      {!collapsed && (
        <ul className="divide-y divide-border border-t border-border">
          {visible.map((it) => (
            <li key={`${it.entity_type}-${it.entity_id}`} className="p-4 flex flex-col md:flex-row md:items-center gap-3" data-testid={`reassign-${it.entity_id}`}>
              <div className="flex-1 min-w-0">
                <button
                  type="button"
                  onClick={() => openEntity(it)}
                  className="text-sm font-medium text-primary hover:underline text-left truncate block w-full"
                  data-testid={`reassign-open-${it.entity_id}`}
                >
                  {it.entity_title || "(untitled)"}
                </button>
                <div className="flex flex-wrap items-center gap-2 mt-1.5 text-[11px]">
                  <span className="text-muted-foreground capitalize">{it.entity_type.replace("_", " ")}</span>
                  <span className="text-muted-foreground">·</span>
                  <span className="inline-flex items-center gap-1 truncate">
                    <span className="text-muted-foreground">Now:</span>
                    <span className="font-medium">{it.current_supplier_name}</span>
                    <span className={`px-1.5 py-0.5 rounded-full font-semibold ${gradeClass(it.current_grade)}`}>
                      <ShieldStar size={9} weight="fill" className="inline -mt-0.5 mr-0.5" />{it.current_grade}
                    </span>
                  </span>
                  <CaretRight size={10} className="text-muted-foreground" />
                  <span className="inline-flex items-center gap-1 truncate">
                    <span className="text-muted-foreground">Upgrade:</span>
                    <span className="font-medium">{it.suggested_supplier_name}</span>
                    <span className={`px-1.5 py-0.5 rounded-full font-semibold ${gradeClass(it.suggested_grade)}`}>
                      <ShieldStar size={9} weight="fill" className="inline -mt-0.5 mr-0.5" />{it.suggested_grade}
                    </span>
                  </span>
                </div>
                {it.area_match && it.area_match.length > 0 && (
                  <p className="text-[10px] text-muted-foreground mt-1 capitalize">
                    Shared areas: {it.area_match.join(", ")}
                  </p>
                )}
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => dismiss(it.entity_id)}
                  className="text-[11px] px-2 py-1 rounded border border-border hover:bg-muted"
                  data-testid={`reassign-dismiss-${it.entity_id}`}
                >
                  <X size={10} className="inline mr-0.5" /> Dismiss
                </button>
                <button
                  type="button"
                  onClick={() => reassign(it)}
                  disabled={busyId === it.entity_id}
                  className="text-[11px] px-2.5 py-1 rounded bg-primary text-primary-foreground font-semibold hover:opacity-90 disabled:opacity-50"
                  data-testid={`reassign-confirm-${it.entity_id}`}
                >
                  {busyId === it.entity_id
                    ? <><Spinner size={10} className="inline animate-spin mr-1" />Reassigning</>
                    : <><CheckCircle size={10} weight="fill" className="inline mr-0.5" /> Reassign</>}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
